import React from 'react';
import { ICV } from '../../interfaces/CV.interface';
import { ILayout, ITheme, SectionName } from '../../interfaces/Theme.interface';
import { getPDFStyles } from '../../utils/theme.util';
import { Document, Page, View } from '@react-pdf/renderer/lib/react-pdf';
import { IPDFStyle } from '../../interfaces/pdfStyle.interface';
import { useTranslation } from 'react-i18next';
import { A4_HEIGHT } from '../BaseTemplate';
import { getSections } from '../Template.util';
import { TemplateName } from '../../common/enum';
import { getPDFSectionElements, IPDFSectionElementsProps } from '../PDFTemplate.util';

interface PDFProps {
    cv: ICV;
    imageSrc: string | null;
    theme: ITheme;
}

const CairoTemplatePDF = (props: PDFProps): React.ReactElement => {
    const { i18n } = useTranslation();

    const isRTL: boolean = i18n.dir() === 'rtl';

    const styles: IPDFStyle = getPDFStyles(props.theme, isRTL);

    const topSectionHeight: number = Math.ceil(A4_HEIGHT * 0.2 * 0.7);

    const templateName: TemplateName = TemplateName.CAIRO;
    const layout: ILayout = getSections(props.theme.layoutTheme, templateName);

    const showTopSection: boolean = layout.top.sections.some((i) => !!i);

    const showMiddleSection: boolean = layout.middle.sections.some((i) => !!i);
    const showMiddleLeftRightSection: boolean = layout.middleLeft.sections.some((i) => !!i) || layout.middleRight.sections.some((i) => !!i);

    const showBottomSection: boolean = layout.bottom.sections.some((i) => !!i);
    const showBottomLeftRightSection: boolean = layout.bottomLeft.sections.some((i) => !!i) || layout.bottomRight.sections.some((i) => !!i);

    const sectionPadding = `0 20px`;

    const getProps = (sections: SectionName[]): IPDFSectionElementsProps => ({
        templateName: templateName,
        sections: sections,
        styles: styles,
        pdfProps: props,
    });

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* top */}
                {showTopSection && (
                    <View style={{ ...styles.rowStyle, padding: sectionPadding }}>
                        {getPDFSectionElements(getProps(layout.top.sections))}
                    </View>
                )}

                {/* top left/right */}
                <View style={{ ...styles.rowStyle }}>
                    <View
                        style={{
                            ...styles.leftTopView,
                            backgroundColor: styles.backgroundColor.leftSection,
                            padding: sectionPadding,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {getPDFSectionElements(getProps(layout.topLeft.sections))}
                    </View>
                    <View
                        style={{
                            ...styles.rightMiddleView,
                            minHeight: topSectionHeight,
                            padding: sectionPadding,
                            alignItems: isRTL ? 'flex-end' : 'flex-start',
                            justifyContent: 'center',
                            alignContent: 'center',
                        }}
                    >
                        {getPDFSectionElements(getProps(layout.topRight.sections))}
                    </View>
                </View>

                {/* middle */}
                {showMiddleSection && (
                    <View style={{ ...styles.rowStyle, padding: sectionPadding }}>
                        {getPDFSectionElements(getProps(layout.middle.sections))}
                    </View>
                )}

                {/* middle left/right */}
                {showMiddleLeftRightSection && (
                    <View style={{ ...styles.rowStyle }}>
                        <View
                            style={{
                                ...styles.leftMiddleView,
                                backgroundColor: styles.backgroundColor.leftSection,
                                padding: sectionPadding,
                                marginTop: '-1px',
                            }}
                        >
                            {getPDFSectionElements(getProps(layout.middleLeft.sections))}
                        </View>

                        <View style={{ ...styles.rightMiddleView, padding: sectionPadding, margin: 0 }}>
                            {getPDFSectionElements(getProps(layout.middleRight.sections))}
                        </View>
                    </View>
                )}

                {/* bottom */}
                {showBottomSection && (
                    <View style={{ ...styles.rowStyle, padding: sectionPadding }}>
                        {getPDFSectionElements(getProps(layout.bottom.sections))}
                    </View>
                )}

                {/* bottom left/right */}
                {showBottomLeftRightSection && (
                    <View style={{ ...styles.rowStyle }}>
                        <View
                            style={{
                                ...styles.leftBottomView,
                                backgroundColor: styles.backgroundColor.leftSection,
                                padding: sectionPadding,
                                marginTop: '-1px',
                            }}
                        >
                            {getPDFSectionElements(getProps(layout.bottomLeft.sections))}
                        </View>

                        <View style={{ ...styles.rightMiddleView, padding: sectionPadding }}>
                            {getPDFSectionElements(getProps(layout.bottomRight.sections))}
                        </View>
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default CairoTemplatePDF;
